import { DecimalPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { PronunciationAssessment } from '../../../../models/quiz';
import { GuidedTourAnchorDirective } from '../../../guided-tour/guided-tour-anchor';
import { toBand, type WordStyle } from '../word-breakdown';

type PronunciationSkillKey = keyof Pick<
  PronunciationAssessment,
  'accuracyScore' | 'fluencyScore' | 'completenessScore' | 'prosodyScore'
>;

export interface PronunciationScore {
  label: string;
  value: number;
  style: WordStyle;
  ringDashArray: string;
}

const MAIN_SCORE_LABEL = 'QUESTIONS.PRONUNCIATION.SCORES.SCORE';

const SKILL_LABELS: { key: PronunciationSkillKey; label: string }[] = [
  { key: 'accuracyScore', label: 'QUESTIONS.PRONUNCIATION.SCORES.ACCURACY' },
  { key: 'fluencyScore', label: 'QUESTIONS.PRONUNCIATION.SCORES.FLUENCY' },
  { key: 'completenessScore', label: 'QUESTIONS.PRONUNCIATION.SCORES.COMPLETENESS' },
  { key: 'prosodyScore', label: 'QUESTIONS.PRONUNCIATION.SCORES.PROSODY' },
];

const MAX_SCORE = 100;

function toScore(label: string, value: number | null): PronunciationScore[] {
  if (typeof value !== 'number') return [];
  const ringValue = Math.min(Math.max(value, 0), MAX_SCORE);
  return [{ label, value, style: toBand(value), ringDashArray: `${ringValue} ${MAX_SCORE}` }];
}

/**
 * The score of a pronunciation assessment, which is the average of its skills, above the score of each skill, each
 * with a ring coloured by its band.
 */
@Component({
  selector: 'nr-pronunciation-scores',
  imports: [DecimalPipe, NgClass, NgTemplateOutlet, TranslatePipe, GuidedTourAnchorDirective],
  templateUrl: './pronunciation-scores.html',
  styleUrl: './pronunciation-scores.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PronunciationScoresComponent {
  readonly assessment = input.required<PronunciationAssessment>();

  readonly mainScore = computed<PronunciationScore | undefined>(
    () => toScore(MAIN_SCORE_LABEL, this.assessment().pronScore)[0],
  );

  readonly skillScores = computed<PronunciationScore[]>(() => {
    const assessment = this.assessment();
    return SKILL_LABELS.flatMap(({ key, label }) => toScore(label, assessment[key]));
  });
}
